<template>
	<div class="Supporters" :class="classes">
		<div class="Supporters-heading">
			<h6 class="Heading">{{ heading }}</h6>
		</div>
		<div class="Supporters-logos" v-for="(logo, index) in logos" :key="index">
			<div class="Supporters-logos-logo">
				<a :href="logo.link"><img :src="logo.src" /></a>
			</div>
			<div v-if="blurb" class="Supporters-blurb">
				<p class="TextSmall TextSmall--tight">{{ blurb }}</p>
			</div>
		</div>
	</div>
</template>

<script>
export default {
	data() {
		return {};
	},
	props: {
		blurb: {
			type: String,
			default: null,
		},
		heading: {
			type: String,
			required: true,
		},
		logos: {
			type: Array,
			required: true,
		},
		horizontal: {
			type: Boolean,
			default: false,
		},
		separator: {
			type: Boolean,
			default: false,
		},
	},
	computed: {
		classes() {
			return {
				'Supporters--horizontal': this.horizontal,
				'Supporters--separator': this.separator,
			};
		},
	},
};
</script>

<style lang="scss">
/* --------------------------------
	PartnerLogos styles
-------------------------------- */

/* Wrapper for a single supporters block */
.Supporters {
	margin: $space-stack-md;
}

/* Optional heading */
.Supporters-heading {
	.Heading {
		color: $color-text-muted;
		font-size: $text-size-xs;
		font-weight: normal;
		margin-bottom: $space-xs;
		text-transform: uppercase;
		@include breakpoint(medium) {
			margin-bottom: $space-sm;
		}
		@include breakpoint(large) {
			font-size: $text-size-sm;
		}
	}
}

/* Group of logos */
.Supporters-logos {
	display: flex;
	flex-wrap: wrap;
	justify-content: space-around;
	@include breakpoint(large) {
		justify-content: flex-start;
	}
	&--center {
		@include breakpoint(large) {
			justify-content: space-around;
		}
	}
}

/* Single logo inside group */
.Supporters-logos-logo {
	align-self: center;
	flex: 0 1 auto;
	margin-right: $space-sm;
	[dir='rtl'] & {
		margin-right: 0;
		margin-left: $space-sm;
	}
	margin-bottom: $space-sm;
	img {
		max-height: 6rem;
		max-width: 9rem;
	}
	@include breakpoint(medium) {
		margin-right: $space-md;
		[dir='rtl'] & {
			margin-right: 0;
			margin-left: $space-md;
		}
		margin-bottom: $space-md;
	}
}

/* Optional blurb */
.Supporters-blurb {
	p {
		@include stack-md;
	}
}

/* An optional wrapper class for displaying multiple Supporters blocks
	Wrap once around multiple blocks */
.SupportersGroup {
	display: flex;
	flex-wrap: wrap;
	> .Supporters {
		flex: 1 1 100%;
		@include breakpoint(medium) {
			flex: 1 1 auto;
			margin: 0;
			&:not(:last-of-type) {
				@include inline-md;
			}
		}
		/* IE10+ styles hack */
		@media all and (-ms-high-contrast: none), (-ms-high-contrast: active) {
			flex: 1 1 100% !important;
		}
	}
	@include breakpoint(medium) {
		flex-wrap: nowrap;
	}
}

/* ----------------
	Variants
---------------- */

/* Separator variant adds a grey line on top,
	then on left at medium breakpoint */
.Supporters--separator {
	border-top: 1px solid $color-neutral-1;
	padding-top: $space-xs;
	@include breakpoint(medium) {
		border-left: 1px solid $color-neutral-1;
		border-top: 0;
		padding-left: $space-sm;
		padding-top: 0;
		[dir='rtl'] & {
			border-left: 0;
			padding-left: 0;
			border-right: 1px solid $color-neutral-1;
			padding-right: $space-sm;
		}
	}
	@include breakpoint(large) {
		padding-left: $space-md;
		[dir='rtl'] & {
			padding-left: 0;
			padding-right: $space-md;
		}
	}
}

/* Centered variant */
.Supporters--centered {
	.Supporters-heading {
		.Heading {
			text-align: center;
		}
	}
	.Supporters-logos {
		justify-content: space-around;
	}
}

/* Horizontal variant makes the Supporters element 
	items show in a horizontal row */
.Supporters--horizontal {
	align-items: center;
	display: flex;
	flex-wrap: wrap;
	.Supporters-heading {
		flex: 1 1 100%;
	}
	.Supporters-logos {
		@include inline-sm;
		flex: 0 1 auto;
	}
	.Supporters-blurb {
		flex: 1 1 50%;
	}
}

/* Border variant for Supporters
	adds outline and padding around a single
	supporters element */
.Supporters--border {
	@include inset-squish-md;
	border: 1px solid $color-neutral-1;
}

/* Border variant for SupportersGroup
	adds outline and padding around group */
.SupportersGroup--border {
	@include inset-squish-md;
	border: 1px solid $color-neutral-1;
}
</style>
